import { ExternalLink, RefreshCw, Shield, Sparkles, Swords, X } from "lucide-react";
import { Component, lazy, Suspense, useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ComponentType, type ReactNode } from "react";
import "./map-v5-overlay.css";
import type { VaelorApi } from "../lib/api";
import type { SessionLease } from "../lib/session-epoch";
import type { HeartChroniclePublicProjection } from "../lib/heart-chronicle-public-projection";
import { privateHeartFrontForSnapshot } from "../lib/heart-front-player-context";
import type { HeartFrontPublicProjection } from "../lib/heart-front-public-projection";
import { HeartFrontSession, type HeartFrontSessionState } from "../lib/heart-front-session";
import { formatSectorLabel } from "../lib/sector-label";
import type { CrestPalette, CrestSigil, GameSnapshot, PlayerCrest, PlayerPortraitUpload, PlayerShowcase, PublicCitadel, PublicPrestige, RaceKey } from "../types";
import type { HeartFront1Id } from "../../../cloudflare/src/game/heart-front1-route";
import type { MapV5OpenBootstrap } from "./MapV5OpenIntent";
import type { MapV5ViewportFeatureProps } from "./MapV5ViewportFeature";
import type { PvpPanelProps } from "./PvpPanel";

const PrestigeCitadel = lazy(() => import("./PrestigeCitadel"));
const HeartFrontFeature = lazy(() => import("./HeartFrontFeature").catch(() => ({
  default: ({ onClose }: { onClose: () => void }) => (
    <HeartFrontLoadState state="error" onClose={onClose} />
  ),
})));
const MAP_V5_BUILD_ENABLED = import.meta.env.VITE_MAP_V5_ENABLED === "1";
const PVP_BUILD_ENABLED = import.meta.env.VITE_PVP_ENABLED === "1";

type HeartFrontLoadStateProps = {
  state: "pending" | "error";
  onClose: () => void;
};

function HeartFrontLoadState({ state, onClose }: HeartFrontLoadStateProps) {
  const failed = state === "error";
  const panelRef = useRef<HTMLElement>(null);

  useEffect(() => {
    panelRef.current?.focus();
  }, [state]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      onClose();
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose]);

  return (
    <section
      ref={panelRef}
      className="heart-front-panel"
      role="dialog"
      aria-modal="true"
      aria-labelledby="heart-front-load-title"
      tabIndex={-1}
      data-testid={failed ? "heart-front-load-error" : "heart-front-load-pending"}
    >
      <div className="heart-front-panel__surface">
        <header className="heart-front-panel__header">
          <div>
            <p className="eyebrow">Campagne du Cœur</p>
            <h2 id="heart-front-load-title">{failed ? "Front du Cœur indisponible" : "Ouverture du Front du Cœur"}</h2>
            <p role={failed ? "alert" : "status"} className="heart-front-panel__intro">
              {failed
                ? "Le Front du Cœur n’a pas pu être chargé. Fermez cette fenêtre pour revenir au jeu ou rechargez la page."
                : "Le Front du Cœur se prépare pour cette première ouverture."}
            </p>
          </div>
          <button
            className="icon-command heart-front-panel__close"
            type="button"
            onClick={onClose}
            style={{ minWidth: 44, minHeight: 44 }}
            aria-label="Fermer le chargement du Front du Cœur"
            title="Fermer"
            data-testid="heart-front-load-close"
          >
            <X size={19} aria-hidden="true" />
          </button>
        </header>
        {failed ? (
          <button
            className="primary-command"
            type="button"
            onClick={() => window.location.reload()}
            style={{ minWidth: 44, minHeight: 44 }}
            data-testid="heart-front-load-reload"
          >
            <RefreshCw size={17} aria-hidden="true" />
            <span>Recharger la page</span>
          </button>
        ) : null}
      </div>
    </section>
  );
}

class HeartFrontFeatureErrorBoundary extends Component<{ fallback: ReactNode; children: ReactNode }, { hasError: boolean }> {
  state = { hasError: false };

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  render() {
    return this.state.hasError ? this.props.fallback : this.props.children;
  }
}

type MapV5BootstrapProps = {
  api: VaelorApi;
  enabled: boolean;
  profileIdentity: { accountId: string; playerId: string; worldId: string };
  snapshot: Extract<GameSnapshot, { initialized: true }>;
  captureSession: () => SessionLease<VaelorApi>;
  isCurrentSession: (lease: SessionLease<VaelorApi>) => boolean;
  frameRef: { current: HTMLIFrameElement | null };
};

function MapV5Trigger({
  label,
  onClick,
  loading,
  retry = false,
}: {
  label: string;
  onClick?: () => void;
  loading?: boolean;
  retry?: boolean;
}) {
  return (
    <button
      className="icon-command map-v5-trigger"
      type="button"
      onClick={onClick}
      title={loading ? undefined : label}
      aria-label={loading ? undefined : label}
      aria-expanded={loading ? undefined : false}
      aria-busy={loading}
      disabled={loading || undefined}
      data-testid="map-v5-open"
    >
      {retry ? <RefreshCw size={17} aria-hidden="true" /> : <i className="map-v5-icon" aria-hidden="true" />}
      <span>{label}</span>
    </button>
  );
}

function MapV5Bootstrap({
  api,
  enabled,
  profileIdentity,
  snapshot,
  captureSession,
  isCurrentSession,
  frameRef,
}: MapV5BootstrapProps) {
  const [mapV5LaunchRequest, setMapV5LaunchRequest] = useState<MapV5OpenBootstrap | null>(null);
  const mapV5ContextToken = useMemo(() => ({}), [
    api,
    enabled,
    snapshot.initialized,
    profileIdentity.accountId,
    profileIdentity.playerId,
    profileIdentity.worldId,
    snapshot.state.gameGenerationId,
    captureSession,
    isCurrentSession,
  ]);
  const featureProps = { api, profileIdentity, snapshot, captureSession, isCurrentSession };

  const openMapV5 = useCallback(() => {
    if (!enabled) return;
    let bail: SessionLease<VaelorApi>;
    try {
      bail = captureSession();
      if (bail.authority !== api || !isCurrentSession(bail)) return;
    } catch {
      return;
    }
    setMapV5LaunchRequest({
      contextToken: mapV5ContextToken,
      bail,
    });
  }, [api, captureSession, enabled, isCurrentSession, mapV5ContextToken]);

  return (
    LazyMapV5ViewportFeature && mapV5LaunchRequest ? (
      <Suspense fallback={
        <>
          <MapV5Trigger label="Chargement de la carte…" loading />
          <span className="map-v5-inline-status" role="status" data-testid="map-v5-import-pending">
            Chargement du module de carte…
          </span>
        </>
      }>
        <LazyMapV5ViewportFeature
          {...featureProps}
          frameRef={frameRef}
          initialOpenBootstrap={mapV5LaunchRequest}
          contextToken={mapV5ContextToken}
          setMapV5LaunchRequest={setMapV5LaunchRequest}
        />
      </Suspense>
    ) : (
      <MapV5Trigger label="Ouvrir la carte stratégique" onClick={openMapV5} loading={false} />
    )
  );
}

type MapV5FeatureLoadProps = MapV5ViewportFeatureProps;

function MapV5EmergencyImportFailure() {
  const reload = () => window.location.reload();
  return (
    <>
      <span className="map-v5-inline-status is-error" role="alert" data-testid="map-v5-import-status">
        Carte indisponible. Rechargez la page pour réessayer.
      </span>
      <MapV5Trigger label="Recharger la page" onClick={reload} retry />
    </>
  );
}

const LazyMapV5ViewportFeature = MAP_V5_BUILD_ENABLED
  ? lazy<ComponentType<MapV5FeatureLoadProps>>(() => import("./MapV5ViewportFeature")
    .catch(() => import("./MapV5ImportFailure"))
    .catch(() => ({ default: MapV5EmergencyImportFailure })))
  : null;

function PvpPanelImportFailure({ onClose }: PvpPanelProps) {
  return (
    <div role="alert" aria-live="assertive">
      Le panneau d’attaque n’a pas pu être chargé. Ferme-le puis recharge la page pour réessayer.
      <button type="button" onClick={onClose}>Fermer</button>
    </div>
  );
}

const LazyPvpPanel = PVP_BUILD_ENABLED
  ? lazy<ComponentType<PvpPanelProps>>(() => import("./PvpPanel")
    .then(({ PvpPanel }) => ({ default: PvpPanel }))
    .catch(() => ({ default: PvpPanelImportFailure })))
  : null;

const BRIDGE_CHANNEL = "vaelor.authority.v1";
type BridgeMovementStartPayload =
  | {
      mission: "harvest" | "pve_camp";
      destination: { q: number; r: number };
      composition: Record<string, number>;
      formation?: string;
      frontId?: never;
    }
  | {
      mission: "a1_supply";
      destination: { q: number; r: number };
      composition: Record<string, number>;
      frontId: HeartFront1Id;
      formation?: never;
    };

type BridgeCommand =
  | { type: "initialize"; payload: { race: RaceKey } }
  | { type: "construction.start"; payload: { buildingKey: string } }
  | { type: "construction.queue"; payload: { buildingKey: string } }
  | { type: "construction.queue.cancel"; payload: Record<string, never> }
  | { type: "research.start"; payload: { researchKey: string } }
  | { type: "research.cancel"; payload: Record<string, never> }
  | { type: "recruitment.start"; payload: { unitKey: string; quantity: number } }
  | { type: "recruitment.cancel"; payload: Record<string, never> }
  | { type: "doctrine.purchase"; payload: { skillId: string } }
  | { type: "movement.start"; payload: BridgeMovementStartPayload }
  | { type: "movement.recall"; payload: { movementCommandId: string } };

type BridgeMessage = {
  channel?: unknown;
  type?: unknown;
  payload?: {
    requestId?: unknown;
    command?: BridgeCommand;
  };
};

export function isEmbeddedChronicleModalMessage(message: unknown): message is {
  channel: typeof BRIDGE_CHANNEL;
  type: "ui-modal";
  payload: { modal: "chronicle"; open: boolean };
} {
  if (!message || typeof message !== "object" || Array.isArray(message)) return false;
  const candidate = message as { channel?: unknown; type?: unknown; payload?: unknown };
  if (Object.keys(candidate).sort().join(",") !== "channel,payload,type" ||
      candidate.channel !== BRIDGE_CHANNEL || candidate.type !== "ui-modal" ||
      !candidate.payload || typeof candidate.payload !== "object" || Array.isArray(candidate.payload)) return false;
  const payload = candidate.payload as { modal?: unknown; open?: unknown };
  return Object.keys(payload).sort().join(",") === "modal,open" &&
    payload.modal === "chronicle" && typeof payload.open === "boolean";
}

export function isBridgeReadyMessage(message: unknown): message is {
  channel: typeof BRIDGE_CHANNEL;
  type: "ready";
  payload: Record<string, never>;
} {
  if (!message || typeof message !== "object" || Array.isArray(message)) return false;
  const candidate = message as { channel?: unknown; type?: unknown; payload?: unknown };
  return Object.keys(candidate).sort().join(",") === "channel,payload,type" &&
    candidate.channel === BRIDGE_CHANNEL && candidate.type === "ready" &&
    !!candidate.payload && typeof candidate.payload === "object" && !Array.isArray(candidate.payload) &&
    Object.keys(candidate.payload).length === 0;
}

function isBridgeCommand(message: BridgeMessage): message is BridgeMessage & {
  type: "command";
  payload: { requestId: string; command: BridgeCommand };
} {
  if (message.channel !== BRIDGE_CHANNEL || message.type !== "command") return false;
  const command = message.payload?.command;
  return typeof message.payload?.requestId === "string" && command !== undefined && typeof command.type === "string";
}

export function portalRootUrl(gameUrl: string): string {
  return new URL("/", gameUrl).toString();
}

export function authorityGameUrl(gameUrl: string): string {
  const url = new URL(gameUrl);
  url.searchParams.set("vaelorAuthority", "1");
  return url.toString();
}

export function createPublicHeartChronicleRefreshScope<Context extends object>(
  fetchProjection: (context: Context) => Promise<HeartChroniclePublicProjection>,
  applyProjection: (projection: HeartChroniclePublicProjection | null) => void,
) {
  let generation = 0;
  let currentContext: Context | null = null;
  let flight: { generation: number; context: Context; promise: Promise<void> } | null = null;

  return {
    activate(context: Context) {
      if (currentContext === context) return;
      generation += 1;
      currentContext = context;
      flight = null;
      applyProjection(null);
    },
    invalidate() {
      if (currentContext !== null || flight !== null) generation += 1;
      currentContext = null;
      flight = null;
      applyProjection(null);
    },
    read(context: Context): Promise<void> {
      if (currentContext !== context) return Promise.resolve();
      if (flight?.generation === generation && flight.context === context) return flight.promise;

      const requestGeneration = generation;
      applyProjection(null);
      let requestFlight: { generation: number; context: Context; promise: Promise<void> };
      const request = fetchProjection(context)
        .then((projection) => {
          if (
            generation === requestGeneration
            && currentContext === context
            && flight === requestFlight
          ) applyProjection(projection);
        })
        .catch(() => {
          if (
            generation === requestGeneration
            && currentContext === context
            && flight === requestFlight
          ) applyProjection(null);
        })
        .finally(() => {
          if (flight === requestFlight) flight = null;
        });
      requestFlight = { generation: requestGeneration, context, promise: request };
      flight = requestFlight;
      return request;
    },
  };
}

export function GameViewport({
  gameUrl,
  webSocketBase,
  api,
  mapV5ProfileIdentity,
  captureSession,
  isCurrentSession,
  snapshot,
  displayName,
  bestRing,
  crest,
  showcase,
  prestige,
  publicCitadels,
  onSaveCrest,
  onSaveShowcase,
  customPortraitUrl,
    onUploadShowcaseImage,
    onOpenPrestige,
    onSnapshot,
    heartFrontOpen,
    onHeartFrontOpenChange,
    embeddedChronicleOpen,
    onEmbeddedChronicleOpenChange,
  mapV5Enabled = MAP_V5_BUILD_ENABLED,
}: {
  gameUrl: string;
  webSocketBase: string;
  api: VaelorApi;
  mapV5ProfileIdentity?: { accountId: string; playerId: string; worldId: string };
  captureSession?: () => SessionLease<VaelorApi>;
  isCurrentSession?: (lease: SessionLease<VaelorApi>) => boolean;
  mapV5Enabled?: boolean;
    snapshot: GameSnapshot;
    displayName: string;
    bestRing: number;
    crest: PlayerCrest | null;
    showcase: PlayerShowcase;
    prestige: PublicPrestige;
    publicCitadels: PublicCitadel[];
    onSaveCrest: (sigil: CrestSigil, palette: CrestPalette) => Promise<PlayerCrest>;
    onSaveShowcase: (
      portrait: PlayerShowcase["portrait"],
      frame: PlayerShowcase["frame"],
      nameColor: PlayerShowcase["nameColor"],
    ) => Promise<PlayerShowcase>;
    customPortraitUrl?: string;
      onUploadShowcaseImage: (image: Blob) => Promise<PlayerPortraitUpload>;
      onOpenPrestige: () => void;
    onSnapshot: (snapshot: GameSnapshot) => void;
    heartFrontOpen: boolean;
    onHeartFrontOpenChange: (open: boolean) => void;
    embeddedChronicleOpen: boolean;
    onEmbeddedChronicleOpenChange: (open: boolean) => void;
}) {
  const [frameVersion, setFrameVersion] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const [prestigeOpen, setPrestigeOpen] = useState(false);
  const [pvpMounted, setPvpMounted] = useState(false);
  const [pvpOpen, setPvpOpen] = useState(false);
  const frameRef = useRef<HTMLIFrameElement>(null);
  const heartFrontTriggerRef = useRef<HTMLButtonElement>(null);
  const pvpTriggerRef = useRef<HTMLButtonElement>(null);
  const heartFrontWasOpenRef = useRef(false);
  const mapV5Active = Boolean(MAP_V5_BUILD_ENABLED && mapV5Enabled && snapshot.initialized &&
    mapV5ProfileIdentity && captureSession && isCurrentSession);
  const toolbarRef = useRef<HTMLDivElement>(null);
  const readyFrameWindowRef = useRef<Window | null>(null);
  const pendingReadyFrameWindowRef = useRef<Window | null>(null);
  const frameLoadAwaitingReadyRef = useRef(false);
  const heartFrontSessionRef = useRef<HeartFrontSession | null>(null);
  const heartFrontSessionEffectGenerationRef = useRef(0);
  const publicHeartFrontMountedRef = useRef(false);
  const publicHeartFrontReadFlightRef = useRef<Promise<void> | null>(null);
  const publicHeartChronicleMountedRef = useRef(false);
  const publicHeartChronicleApiRef = useRef(api);
  publicHeartChronicleApiRef.current = api;
  const publicHeartChronicleFrameContextRef = useRef<{ frameWindow: Window } | null>(null);
  if (!heartFrontSessionRef.current) {
    heartFrontSessionRef.current = new HeartFrontSession(api, webSocketBase);
  }
  const heartFrontSession = heartFrontSessionRef.current;
  const [heartFrontSessionState, setHeartFrontSessionState] = useState<HeartFrontSessionState>(() => heartFrontSession.getState());
  const [publicHeartFrontProjection, setPublicHeartFrontProjection] = useState<HeartFrontPublicProjection>();
  const [publicHeartChronicleProjection, setPublicHeartChronicleProjection] = useState<HeartChroniclePublicProjection | null>(null);
  const publicHeartChronicleRefreshScopeRef = useRef<ReturnType<typeof createPublicHeartChronicleRefreshScope> | null>(null);
  if (!publicHeartChronicleRefreshScopeRef.current) {
    publicHeartChronicleRefreshScopeRef.current = createPublicHeartChronicleRefreshScope(
      () => publicHeartChronicleApiRef.current.heartChronicle(),
      (projection) => {
        if (publicHeartChronicleMountedRef.current) setPublicHeartChronicleProjection(projection);
      },
    );
  }
  const publicHeartChronicleRefreshScope = publicHeartChronicleRefreshScopeRef.current;
  const gameOrigin = useMemo(() => new URL(gameUrl).origin, [gameUrl]);
  const portalUrl = useMemo(() => portalRootUrl(gameUrl), [gameUrl]);
  const embeddedGameUrl = useMemo(() => authorityGameUrl(gameUrl), [gameUrl]);
  const activeFrontId = useMemo(() => privateHeartFrontForSnapshot(snapshot), [snapshot]);
  const sectorLabel = formatSectorLabel(snapshot.initialized ? snapshot.state.location?.sectorId : undefined);

  useEffect(() => {
    if (embeddedChronicleOpen) setPrestigeOpen(false);
  }, [embeddedChronicleOpen]);

  useLayoutEffect(() => {
    const toolbar = toolbarRef.current;
    if (!toolbar) return;
    if (embeddedChronicleOpen) toolbar.setAttribute("inert", "");
    else toolbar.removeAttribute("inert");
  }, [embeddedChronicleOpen]);

  useLayoutEffect(() => {
    if (heartFrontOpen) {
      heartFrontWasOpenRef.current = true;
      return;
    }
    if (!heartFrontWasOpenRef.current) return;
    heartFrontWasOpenRef.current = false;
    heartFrontTriggerRef.current?.focus();
  }, [heartFrontOpen]);

  const closeHeartFront = useCallback(() => onHeartFrontOpenChange(false), [onHeartFrontOpenChange]);
  const closePvp = useCallback(() => {
    setPvpOpen(false);
    window.requestAnimationFrame(() => pvpTriggerRef.current?.focus());
  }, []);

  const readPublicHeartFronts = useCallback((): Promise<void> => {
    if (publicHeartFrontReadFlightRef.current !== null) return publicHeartFrontReadFlightRef.current;

    setPublicHeartFrontProjection(undefined);
    const request = api.heartFronts()
      .then((projection) => {
        if (publicHeartFrontMountedRef.current) setPublicHeartFrontProjection(projection);
      })
      .catch(() => {
        if (publicHeartFrontMountedRef.current) setPublicHeartFrontProjection(undefined);
      })
      .finally(() => {
        if (publicHeartFrontReadFlightRef.current === request) publicHeartFrontReadFlightRef.current = null;
      });
    publicHeartFrontReadFlightRef.current = request;
    return request;
  }, [api]);

  const readPublicHeartChronicle = useCallback((frameWindow: Window): Promise<void> => {
    const context = publicHeartChronicleFrameContextRef.current;
    if (!context || context.frameWindow !== frameWindow) return Promise.resolve();
    return publicHeartChronicleRefreshScope.read(context);
  }, [publicHeartChronicleRefreshScope]);

  const invalidatePublicHeartChronicle = useCallback(() => {
    publicHeartChronicleFrameContextRef.current = null;
    publicHeartChronicleRefreshScope.invalidate();
  }, [publicHeartChronicleRefreshScope]);

  const activatePublicHeartChronicleFrame = useCallback((frameWindow: Window) => {
    const current = publicHeartChronicleFrameContextRef.current;
    if (current?.frameWindow === frameWindow) return;
    const context = { frameWindow };
    publicHeartChronicleFrameContextRef.current = context;
    publicHeartChronicleRefreshScope.activate(context);
  }, [publicHeartChronicleRefreshScope]);

  useEffect(() => {
    const effectGeneration = ++heartFrontSessionEffectGenerationRef.current;
    const unsubscribe = heartFrontSession.subscribe(setHeartFrontSessionState);
    heartFrontSession.configure(api, webSocketBase, activeFrontId);
    heartFrontSession.start();
    return () => {
      unsubscribe();
      queueMicrotask(() => {
        if (heartFrontSessionEffectGenerationRef.current === effectGeneration) heartFrontSession.stop();
      });
    };
  }, [activeFrontId, api, heartFrontSession, webSocketBase]);

  useEffect(() => {
    publicHeartFrontMountedRef.current = true;
    void readPublicHeartFronts();
    return () => {
      publicHeartFrontMountedRef.current = false;
    };
  }, [readPublicHeartFronts]);

  useEffect(() => {
    publicHeartChronicleMountedRef.current = true;
    const readyFrameWindow = readyFrameWindowRef.current;
    if (readyFrameWindow) activatePublicHeartChronicleFrame(readyFrameWindow);
    return () => {
      publicHeartChronicleMountedRef.current = false;
      invalidatePublicHeartChronicle();
    };
  }, [activatePublicHeartChronicleFrame, invalidatePublicHeartChronicle]);

  const send = useCallback((type: "snapshot" | "identity" | "public-citadels" | "heart-fronts" | "heart-chronicle" | "error", payload: object | null) => {
    frameRef.current?.contentWindow?.postMessage({ channel: BRIDGE_CHANNEL, type, payload }, gameOrigin);
  }, [gameOrigin]);

  const sendSnapshot = useCallback((nextSnapshot: GameSnapshot) => {
    send("snapshot", { snapshot: nextSnapshot, displayName });
  }, [displayName, send]);

  const sendIdentity = useCallback(() => {
    send("identity", { displayName });
  }, [displayName, send]);

  const sendPublicCitadels = useCallback(() => {
    const frameWindow = frameRef.current?.contentWindow;
    if (!frameWindow || readyFrameWindowRef.current !== frameWindow) return;
    send("public-citadels", { citadels: publicCitadels });
  }, [publicCitadels, send]);

  const sendHeartFrontProjection = useCallback(() => {
    const frameWindow = frameRef.current?.contentWindow;
    if (!frameWindow || readyFrameWindowRef.current !== frameWindow || !publicHeartFrontProjection) return;
    send("heart-fronts", publicHeartFrontProjection);
  }, [publicHeartFrontProjection, send]);

  const sendHeartChronicleProjection = useCallback(() => {
    const frameWindow = frameRef.current?.contentWindow;
    if (!frameWindow || readyFrameWindowRef.current !== frameWindow) return;
    send("heart-chronicle", publicHeartChronicleProjection);
  }, [publicHeartChronicleProjection, send]);

  const activateReadyFrame = useCallback((frameWindow: Window) => {
    if (frameRef.current?.contentWindow !== frameWindow) return;
    activatePublicHeartChronicleFrame(frameWindow);
    readyFrameWindowRef.current = frameWindow;
    pendingReadyFrameWindowRef.current = null;
    frameLoadAwaitingReadyRef.current = false;
    send("heart-chronicle", null);
    sendSnapshot(snapshot);
    sendIdentity();
    sendPublicCitadels();
    sendHeartFrontProjection();
  }, [activatePublicHeartChronicleFrame, send, sendHeartFrontProjection, sendIdentity, sendPublicCitadels, sendSnapshot, snapshot]);

  useEffect(() => {
    sendPublicCitadels();
  }, [sendPublicCitadels]);

  useEffect(() => {
    sendHeartFrontProjection();
  }, [sendHeartFrontProjection]);

  useEffect(() => {
    sendHeartChronicleProjection();
  }, [sendHeartChronicleProjection]);

  useEffect(() => {
    if (loaded) {
      sendSnapshot(snapshot);
      sendIdentity();
    }
  }, [loaded, sendIdentity, sendSnapshot, snapshot]);

  useEffect(() => {
    const onMessage = (event: MessageEvent<unknown>) => {
      if (event.origin !== gameOrigin || event.source !== frameRef.current?.contentWindow) return;
      const message = event.data;
      if (isBridgeReadyMessage(message)) {
        const frameWindow = event.source as Window;
        if (readyFrameWindowRef.current === frameWindow) return;
        pendingReadyFrameWindowRef.current = frameWindow;
        onEmbeddedChronicleOpenChange(false);
        if (frameLoadAwaitingReadyRef.current) activateReadyFrame(frameWindow);
        return;
      }
      if (isEmbeddedChronicleModalMessage(message)) {
        if (readyFrameWindowRef.current !== event.source) return;
        onEmbeddedChronicleOpenChange(message.payload.open);
        if (message.payload.open) void readPublicHeartChronicle(event.source as Window);
        return;
      }
      const commandMessage = message as BridgeMessage;
      if (!isBridgeCommand(commandMessage)) return;
      const { requestId, command } = commandMessage.payload;
      const run = async () => {
        try {
          let response;
          switch (command.type) {
            case "initialize":
              response = await api.initializeGame(command.payload.race, requestId);
              break;
            case "construction.start":
              response = await api.startBuilding(command.payload.buildingKey, requestId);
              break;
            case "construction.queue":
              response = await api.queueBuilding(command.payload.buildingKey, requestId);
              break;
            case "construction.queue.cancel":
              response = await api.cancelQueuedBuilding(requestId);
              break;
            case "research.start":
              response = await api.startResearch(command.payload.researchKey, requestId);
              break;
            case "research.cancel":
              response = await api.cancelResearch(requestId);
              break;
            case "recruitment.start":
              response = await api.startRecruitment(command.payload.unitKey, command.payload.quantity, requestId);
              break;
            case "recruitment.cancel":
              response = await api.cancelRecruitment(requestId);
              break;
            case "doctrine.purchase":
              response = await api.purchaseDoctrine(command.payload.skillId, requestId);
              break;
            case "movement.start":
              response = await api.startMovement(
                command.payload.destination,
                command.payload.composition,
                command.payload.mission,
                requestId,
                command.payload.formation,
                command.payload.frontId,
              );
              break;
            case "movement.recall":
              response = await api.recallMovement(command.payload.movementCommandId, requestId);
              break;
            default:
              return;
          }
          onSnapshot(response.snapshot);
          sendSnapshot(response.snapshot);
        } catch (cause) {
          send("error", {
            requestId,
            message: cause instanceof Error ? cause.message : "Ordre refusé par le Bastion.",
          });
        }
      };
      void run();
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [activateReadyFrame, api, gameOrigin, invalidatePublicHeartChronicle, onEmbeddedChronicleOpenChange, onSnapshot, readPublicHeartChronicle, send, sendSnapshot]);

  const handleFrameLoad = useCallback(() => {
    const frameWindow = frameRef.current?.contentWindow ?? null;
    readyFrameWindowRef.current = null;
    frameLoadAwaitingReadyRef.current = true;
    onEmbeddedChronicleOpenChange(false);
    invalidatePublicHeartChronicle();
    setLoaded(true);
    if (frameWindow && pendingReadyFrameWindowRef.current === frameWindow) activateReadyFrame(frameWindow);
  }, [activateReadyFrame, invalidatePublicHeartChronicle, onEmbeddedChronicleOpenChange]);

  const reloadGame = useCallback(async () => {
    onEmbeddedChronicleOpenChange(false);
    invalidatePublicHeartChronicle();
    readyFrameWindowRef.current = null;
    pendingReadyFrameWindowRef.current = null;
    frameLoadAwaitingReadyRef.current = false;
    setLoaded(false);
    try {
      const response = await api.gameBootstrap();
      onSnapshot(response.snapshot);
      readyFrameWindowRef.current = null;
      setFrameVersion((version) => version + 1);
    } catch {
      setLoaded(true);
    }
  }, [api, invalidatePublicHeartChronicle, onEmbeddedChronicleOpenChange, onSnapshot]);

  return (
    <section className={mapV5Active ? "game-viewport game-viewport--map-v5" : "game-viewport"} aria-label="Jeu VAELOR">
      <div
        ref={toolbarRef}
        className="viewport-toolbar"
        aria-hidden={embeddedChronicleOpen || pvpOpen ? true : undefined}
      >
        <span><span className={`status-dot ${loaded ? "is-online" : ""}`} /> {loaded ? "Jeu chargé" : "Chargement du Bastion"}</span>
        <div>
          {MAP_V5_BUILD_ENABLED && mapV5Enabled && snapshot.initialized && mapV5ProfileIdentity &&
            captureSession && isCurrentSession && (
              <MapV5Bootstrap
                api={api}
                enabled={mapV5Enabled}
                profileIdentity={mapV5ProfileIdentity}
                snapshot={snapshot}
                captureSession={captureSession}
                isCurrentSession={isCurrentSession}
                frameRef={frameRef}
              />
            )}
          <button
            ref={heartFrontTriggerRef}
            className={`icon-command heart-front-trigger ${heartFrontOpen ? "is-active" : ""}`}
            type="button"
            onClick={() => onHeartFrontOpenChange(true)}
            title="Ouvrir le Front du Cœur"
            aria-label="Ouvrir le Front du Cœur"
            aria-pressed={heartFrontOpen}
          >
            <Shield size={17} aria-hidden="true" />
            <span>Front du Cœur</span>
          </button>
          {PVP_BUILD_ENABLED && snapshot.initialized && captureSession && isCurrentSession && (
            <button
              ref={pvpTriggerRef}
              className={`icon-command ${pvpOpen ? "is-active" : ""}`}
              type="button"
              style={{ minWidth: 44, minHeight: 44 }}
              onClick={() => {
                onHeartFrontOpenChange(false);
                setPrestigeOpen(false);
                setPvpMounted(true);
                setPvpOpen(true);
              }}
              title="Attaquer un Bastion"
              aria-label="Attaquer un Bastion"
              aria-haspopup="dialog"
              aria-expanded={pvpOpen}
            >
              <Swords size={17} aria-hidden="true" />
            </button>
          )}
          <button
            className="icon-command prestige-command"
            type="button"
            onClick={() => { onOpenPrestige(); setPrestigeOpen(true); }}
            title="Contempler la Citadelle"
            aria-label="Contempler la Citadelle en vue Prestige"
          >
            <Sparkles size={17} />
          </button>
          <button
            className="icon-command"
            type="button"
            onClick={() => { void reloadGame(); }}
            title="Recharger le jeu"
            aria-label="Recharger le jeu"
          >
            <RefreshCw size={17} />
          </button>
          <a
            className="icon-command"
            href={portalUrl}
            target="_blank"
            rel="noreferrer"
            title="Ouvrir le portail authentifié dans un nouvel onglet"
            aria-label="Ouvrir le portail authentifié dans un nouvel onglet"
          >
            <ExternalLink size={17} />
          </a>
        </div>
      </div>
      <iframe
        ref={frameRef}
        key={frameVersion}
        className="game-frame"
        src={embeddedGameUrl}
        title="VAELOR — monde de jeu"
        aria-hidden={pvpOpen ? true : undefined}
        onLoad={handleFrameLoad}
        referrerPolicy="strict-origin-when-cross-origin"
        sandbox="allow-downloads allow-forms allow-modals allow-pointer-lock allow-same-origin allow-scripts"
      />
      {PVP_BUILD_ENABLED && pvpMounted && LazyPvpPanel && captureSession && isCurrentSession && (
        <Suspense fallback={<div className="pvp-panel__loading" role="status">Chargement du panneau d’attaque…</div>}>
          <LazyPvpPanel
            gameUrl={gameUrl}
            open={pvpOpen}
            api={api}
            captureSession={captureSession}
            isCurrentSession={isCurrentSession}
            snapshot={snapshot}
            onSnapshot={onSnapshot}
            onClose={closePvp}
          />
        </Suspense>
      )}
      {heartFrontOpen && (
        <HeartFrontFeatureErrorBoundary
          fallback={<HeartFrontLoadState state="error" onClose={closeHeartFront} />}
        >
          <Suspense fallback={<HeartFrontLoadState state="pending" onClose={closeHeartFront} />}>
            <HeartFrontFeature
              api={api}
              session={heartFrontSession}
              sessionState={heartFrontSessionState}
              publicProjection={publicHeartFrontProjection}
              snapshot={snapshot}
              bestRing={bestRing}
              onSnapshot={onSnapshot}
              onClose={closeHeartFront}
            />
          </Suspense>
        </HeartFrontFeatureErrorBoundary>
      )}
      {prestigeOpen && (
        <Suspense fallback={<div className="prestige-loading" role="status">La Citadelle émerge du Miasme…</div>}>
            <PrestigeCitadel
              displayName={displayName}
              sectorLabel={sectorLabel}
              bestRing={bestRing}
              people={snapshot.initialized ? snapshot.state.race : null}
              authoritativeState={snapshot.initialized ? snapshot.state : null}
              crest={crest}
              showcase={showcase}
              prestige={prestige}
              publicCitadels={publicCitadels}
              onSaveCrest={onSaveCrest}
              onSaveShowcase={onSaveShowcase}
              customPortraitUrl={customPortraitUrl}
              onUploadShowcaseImage={onUploadShowcaseImage}
              onClose={() => setPrestigeOpen(false)}
          />
        </Suspense>
      )}
    </section>
  );
}
